import { useMemo } from "react";
import { to_markdown } from "jira2md";
import { EntityTypes } from "@vzbl/integrations";
import { useApplicationContext } from "src/hooks/useApplication";
import { Integrations } from "src/search/lib/useIntegrations";
import { useEntityContext } from "../useEntityContext";
import { TabbedWork } from "../components/TabbedWork";
import { generateWorkTabs } from "../components/worktabs";
import { Work, WorkCommentsComponent, WorkComponent } from "../Work";
import { TimeMachine, TimeMachineComponent } from "../TimeMachine";
import { Insights } from "../Insights";
import { EntityScreen } from "./components/layout/Screen2";
import { EntityTabs } from "./components/EntityTabs";
import { Tooltip } from "antd";
import { IssueOverview } from "./components/IssueOverview";
import type { TabsProps } from "antd";
import { ReportsLinks } from "../components/Reports";
import { BetaTag } from "src/components/BetaTag";

/**
 * IssueEntity Component
 * Renders the main issue view with tabs for Overview, Analytics, Related Work, Teamwork and History
 * Handles the display of issue details and related information in an organized tab structure
 */
export const IssueEntity = () => {
  // Context and data setup
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entity = context.dataRef.current.entity;
  const properties = entity.properties;

  const { description, integration } = properties;

  // Convert Jira description to markdown if needed
  const markdown = useMemo(() => {
    if (description && integration === Integrations.JIRA) {
      return to_markdown(description);
    }
    return description;
  }, [description, integration]);

  // Define tab structure and content
  const tabItems: TabsProps["items"] = [
    {
      key: "overview",
      label: "Overview",
      children: <IssueOverview entity={entity} />,
    },
    {
      key: "reports",
      id: "reports",
      label: (
        <>
          Reports <BetaTag size="small" />
        </>
      ),
      children: (
        <ReportsLinks
          filterable={EntityTypes.Issue}
          filter={properties.issueKey}
          defaultTimeframe={{
            duration: "P30D",
          }}
        />
      ),
    },
    {
      key: "2",
      label: "Related Work",
      children: (
        <>
          <TabbedWork
            items={generateWorkTabs(orgId, properties.uuid, [
              { entityTypes: EntityTypes.Commit },
              {
                entityTypes: EntityTypes.PullRequest,
                label: "Pull Requests",
              },
              {
                entityTypes: EntityTypes.CicdPipeline,
                label: "Deployments",
              },
              { entityTypes: EntityTypes.Worklog },
            ])}
          />
          <br />
          <Work orgId={orgId} entityUuid={properties.uuid} types={["Issue"]}>
            <WorkComponent title="Related Issues" />
          </Work>
        </>
      ),
    },
    {
      key: "analytics",
      label: "Analytics",
      children: <Insights entity={entity} />,
    },
    {
      key: "3",
      label: "Communication",
      children: (
        <>
          <Work
            orgId={orgId}
            entityUuid={properties.uuid}
            types={[EntityTypes.IssueComment]}
          >
            <WorkCommentsComponent title="Comments" />
          </Work>
          <br />
          <TabbedWork
            title="Communications"
            items={generateWorkTabs(orgId, properties.uuid, [
              {
                entityTypes: EntityTypes.CalendarEntry,
                label: "Meetings",
              },
              { entityTypes: EntityTypes.Message },
            ])}
          />
        </>
      ),
    },

    {
      key: "5",
      label: "History",
      children: (
        <TimeMachine>
          <TimeMachineComponent />
        </TimeMachine>
      ),
    },
  ];

  // Properties to exclude from the dynamic properties panel
  const propertiesSkipList = [
    "issueType",
    "priority",
    "status",
    "title",
    "name",
    "summary",
    "description",
    "vzblClassification",
    "vzblClassificationExplanation",
    "vzblValueStatement",
    "vzblMissingDetails",
    "vzblUnansweredQuestions",
    "vzblClarityScore",
    "vzblClarityExplanation",
    "vzblFrictionScore",
    "vzblFrictionExplanation",
    "vzblImpact",
    "vzblImpactExplanation",
    "vzblUrgency",
    "vzblUrgencyExplanation",
  ];

  return (
    <EntityScreen>
      <EntityTabs
        propertiesSkipList={propertiesSkipList}
        entity={entity}
        tabs={tabItems}
      />
    </EntityScreen>
  );
};
